import { useEffect, useState } from 'react';
import { Plus, FolderPlus, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/lib/AuthContext';

/**
 * "Add to catalog" button — opens a dialog listing the caller's catalogs plus
 * an inline "create new catalog" input. Used on Product Detail page.
 */
export default function AddToCatalogButton({ sku, compact = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  const canUse = user && (user.role === 'admin' || user.status === 'active');
  if (!canUse) return null;

  // Block ProductCard's parent <Link> navigation when the button is embedded
  // inside a linked card.
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/catalogs');
      setCatalogs(data.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const addTo = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post('/catalogs/add-item', { sku, ...payload });
      toast.success(data.created ? 'Catalog created + item added' : `Added — ${data.item_count} items`);
      setOpen(false);
      setNewName('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { stop(e); setOpen(true); }}
        className={compact
          ? "absolute top-2 right-2 z-10 p-2 rounded-full bg-white/95 backdrop-blur border border-neutral-200 text-neutral-700 hover:text-[color:var(--ije-pink-dark)] hover:border-[color:var(--ije-pink)] shadow-sm transition-colors"
          : "btn-outline"}
        title="Add to catalog"
        data-testid="add-to-catalog-btn"
      >
        <FolderPlus size={compact ? 15 : 14} />{!compact && ' Add to catalog'}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-to-catalog-dialog" onClick={stop}>
          <DialogHeader>
            <DialogTitle className="h-display text-2xl">Add to catalog</DialogTitle>
            <p className="sans text-xs uppercase tracking-widest text-neutral-500">{sku}</p>
          </DialogHeader>

          <div className="mt-3">
            <p className="eyebrow mb-2">Existing catalogs</p>
            {loading ? (
              <p className="sans text-sm text-neutral-500">Loading…</p>
            ) : catalogs.length === 0 ? (
              <p className="sans text-sm text-neutral-500">No catalogs yet — create one below.</p>
            ) : (
              <ul className="border border-neutral-200 divide-y divide-neutral-100 max-h-56 overflow-y-auto">
                {catalogs.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => addTo({ catalog_id: c.id })}
                      className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center justify-between"
                      data-testid={`add-to-${c.id}`}
                    >
                      <span className="sans text-sm text-neutral-900">{c.name}</span>
                      <span className="sans text-xs text-neutral-500">{(c.item_skus || []).length} items</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-5">
            <p className="eyebrow mb-2">Or create new</p>
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && newName.trim() && addTo({ new_catalog_name: newName.trim() })}
                placeholder="Catalog name"
                className="ije-input h-10 flex-1"
                data-testid="new-catalog-name-inline"
              />
              <button
                type="button"
                onClick={() => addTo({ new_catalog_name: newName.trim() })}
                disabled={busy || !newName.trim()}
                className="btn-primary h-10"
                data-testid="add-to-catalog-create"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              </button>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline h-10">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
